import { useTrackFailedToUploadAsset, useTrackUploadedAsset } from '@air/analytics';
import { ClipSource } from '@air/api/types';
import { ContentCollectionView, setViewAction } from '@air/feature-content-collection/redux';
import { isLarge, uploadByIdSelector } from '@air/redux-uploader';
import { useUploadManager, UseUploadManagerProps } from '@air/upload-manager';
import { isAbortError } from '@air/upload-utils';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { UploadTaskParams } from '~/components/Upload/hooks/types';
import { useFetchClipForContentCollectionUpload } from '~/components/Upload/publicUpload/useFetchClipForContentCollectionUpload';
import { useGetLargePublicUploadTasks } from '~/components/Upload/publicUpload/useGetLargePublicUploadTasks';
import { useGetSmallPublicUploadTask } from '~/components/Upload/publicUpload/useGetSmallPublicUploadTask';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export const useBoardCollectionUploadsManager = () => {
  const store = useAirStore();
  const dispatch = useDispatch();
  const { fetchClipForContentCollectionUpload } = useFetchClipForContentCollectionUpload();
  const { trackUploadedAsset } = useTrackUploadedAsset();
  const { trackFailedToUploadAsset } = useTrackFailedToUploadAsset();

  const { getSmallUploadTask } = useGetSmallPublicUploadTask();
  const { getLargeUploadTasks } = useGetLargePublicUploadTasks();

  const getUploadTasks = useCallback<UseUploadManagerProps['getUploadTasks']>(
    ({ upload }) => {
      const params: UploadTaskParams = {
        uploadId: upload.id,
        onUploadFinished: ({ uploadId }) => {
          const upload = uploadByIdSelector(store.getState(), uploadId);
          upload && trackUploadedAsset({ clipId: upload.clipId });
          dispatch(setViewAction({ view: ContentCollectionView.Manage }));
        },
        onUploadStarted: ({ uploadId }) => fetchClipForContentCollectionUpload(uploadId),
        onError: ({ upload, error }) => {
          const _error = reportErrorToBugsnag({ error, context: 'Error uploading file' });
          if (!isAbortError(_error)) {
            trackFailedToUploadAsset({
              id: upload.id,
              type: upload.apiUploadData.type,
              source: ClipSource.web,
              error: _error.message,
            });
          }
        },
      };

      if (isLarge(upload)) {
        return getLargeUploadTasks(params);
      } else {
        return [getSmallUploadTask(params)];
      }
    },
    [
      fetchClipForContentCollectionUpload,
      getLargeUploadTasks,
      getSmallUploadTask,
      store,
      trackFailedToUploadAsset,
      trackUploadedAsset,
    ],
  );

  return useUploadManager({ getUploadTasks });
};
